import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper, Stack } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  moduleName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.moduleName ? ` – ${this.props.moduleName}` : ''}]`, error, info.componentStack);
  }

  handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh" p={3}>
        <Paper elevation={0} sx={{ p: 4, maxWidth: 520, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Stack spacing={2} alignItems="center">
            <ErrorOutline sx={{ fontSize: 48, color: '#ef4444' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Something went wrong
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {this.state.error?.message || 'An unexpected error occurred.'}
            </Typography>
            <Button variant="outlined" size="small" onClick={this.handleReset} sx={{ mt: 1 }}>
              Try Again
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }
}

export default ErrorBoundary;
